import { SasApprovalAlert } from '@/components/banners/SasApprovalAlert';
import { BrazeImageCard } from '@/components/braze/BrazeImageCard';
import ErrorPage from '@/components/error/ErrorPage';
import { BackButton } from '@/components/order/BackButton';
import CheckoutOrderSummary from '@/components/order/order-summary/CheckoutOrderSummary';
import ConsentCheckbox from '@/components/order/order-summary/ConsentCheckbox';
import type { DispenseIntervalDelayAcceptanceProps } from '@/components/order/order-summary/DispenseIntervalDelayAcceptance';
import { DispenseIntervalDelayAcceptance } from '@/components/order/order-summary/DispenseIntervalDelayAcceptance';
import UmedsMarketingConsent from '@/components/order/order-summary/UmedsMarketingConsent';
import { PharmacyAllocationDispenseIntervalAlert } from '@/components/order/pharmacy-allocation/PharmacyAllocationDispenseIntervalAlert';
import { CheckoutButton } from '@/components/order/shippingOptions/CheckoutButton';
import ManualShippingAddress from '@/components/order/shippingOptions/MannualShippingAddress';
import ShippingAddress from '@/components/order/shippingOptions/ShippingAddress';
import { ClickAndCollectShippingOptionCard } from '@/components/order/shippingOptions/ShippingOptionCard/ClickAndCollectShippingOptionCard';
import LoadingShippingOptionCard from '@/components/order/shippingOptions/ShippingOptionCard/LoadingShippingOptionCard';
import { SameDayAfternoonShippingOptionCard } from '@/components/order/shippingOptions/ShippingOptionCard/SameDayAfternoonShippingOptionCard';
import { SameDayDeliveryShippingOptionCard } from '@/components/order/shippingOptions/ShippingOptionCard/SameDayDeliveryShippingOptionCard';
import { StandardDeliveryShippingOptionCard } from '@/components/order/shippingOptions/ShippingOptionCard/StandardShippingOptionCard';
import ValidIDCheckbox from '@/components/order/shippingOptions/ValidIDCheckbox';
import { UseOrDeleteExistingOrderModal } from '@/components/order/UseOrDeleteExistingOrderModal';
import { PurchaseStepper } from '@/components/products/purchaseStepper/PurchaseStepper';
import settings from '@/constants/constants';
import { FF_BRAZE_CONTENT_CARDS, FF_ESCRIPT_RECONCILE_PRESCRIPTIONS } from '@/constants/featureFlags';
import { useReconcilePrescriptionsFFWrapper } from '@/hooks/escript/useReconcilePrescriptions';
import { useCheckout } from '@/hooks/order/useCheckout';
import { useClearStaleShippingOptionSelection } from '@/hooks/order/useClearStaleShippingOptionSelection';
import useClickAndCollectDeliveryOption from '@/hooks/order/useClickAndCollectDeliveryOption';
import useClickAndCollectLocationQuery, { userAddressToAddress } from '@/hooks/order/useClickAndCollectLocationQuery';
import useDeliveryOptions from '@/hooks/order/useDeliveryOptions';
import useSameDayAfternoonDeliveryOption from '@/hooks/order/useSameDayAfternoonDeliveryOption';
import useSameDayDeliveryOption from '@/hooks/order/useSameDayDeliveryOption';
import useStandardDeliveryOption from '@/hooks/order/useStandardDeliveryOption';
import getCheckoutData from '@/hooks/patient/refill/getCheckoutData';
import type { PersistedProductDetails } from '@/hooks/patient/refill/persistedOrderDetails';
import { managePersistedOrderDetails } from '@/hooks/patient/refill/persistedOrderDetails';
import { useShippingAddress } from '@/hooks/patient/refill/useShippingAddress';
import useShopifyCheckout from '@/hooks/patient/refill/useShopifyCheckout';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import useCustomer from '@/hooks/user/useCustomer';
import { PATIENT_CLICK_AND_COLLECT, UMEDS_MARKETING } from '@/routes/route-paths';
import { Logger } from '@/utils/logger';
import { DELIVERY_OPTION, DELIVERY_OPTIONS, translateMontuError, usePrevious } from '@montugroup/circuit-shared';
import { getTsApiV1Client } from '@montugroup/circuit-shared/api/api-v1.client';
import useUserProfileData from '@montugroup/circuit-shared/hooks/user/useGetUserProfile';
import type { Address } from '@montugroup/circuit-shared/types/address';
import { isMontuError } from '@montugroup/circuit-shared/utils/typscript-helpers';
import { useEmitUiEvent } from '@montugroup/data-collection';
import { Box, Link, Stack, toast, Typography } from '@montugroup/design-system';
import { FormHelperText, Grid2, styled } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { isErrorResponse } from '@ts-rest/core';
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Controller, FormProvider } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatDateForDisplay } from '../umeds/order-medication/util';

type ShippingOptionsFormValues = Address & {
  shippingOption?: (typeof DELIVERY_OPTIONS)[DELIVERY_OPTION]['name'];
};

const PageContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(5),
  alignSelf: 'stretch',
}));

const PageSection = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

const TermsConditionCheckboxContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const logger = new Logger('ShippingOptions');

const client = getTsApiV1Client();

export function ShippingOptions() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const emitEvent = useEmitUiEvent();
  const { data: customer, isLoading: isCustomerLoading } = useCustomer();
  const { setLocationQuery } = useClickAndCollectLocationQuery();
  const { flags } = useFeatureFlags();
  const [checkoutError, setCheckoutError] = useState<unknown>(null);
  const { addressValidity, isAddressValidityLoading } = useCheckout();

  const persistedOrderDetails = managePersistedOrderDetails();
  const selectedData = persistedOrderDetails.getOrderDetails();
  const selectedProducts = selectedData?.selectedProducts;
  const discounts = selectedData?.discounts;

  const transformedProductsForSummary =
    selectedProducts?.map((product) => ({
      id: product.id,
      data: {
        product_id: product.id,
        product_name: product.name,
        price: product.price,
        quantity: product.quantity,
        brand: product.brand,
        is_concession: product.is_concession,
      },
    })) ?? [];

  const checkoutData = getCheckoutData(transformedProductsForSummary, discounts ?? []);
  const prescriptionIds = useMemo(() => {
    return (selectedProducts?.map((product) => product.prescriptionId) ?? []).filter(Boolean) as number[]; // Using as cast because .filter(Boolean) removes undefined posibility
  }, [selectedProducts]);

  const {
    query: { isLoading: isReconcilingPrescriptions, isError: isReconcilingPrescriptionsError },
    reconcilePrescriptionsData,
    mutate: mutateReconcilePrescriptions,
  } = useReconcilePrescriptionsFFWrapper(prescriptionIds);

  const { data: userProfileData, getUserState } = useUserProfileData();
  const userAddressAsAddress = useMemo(() => {
    if (!userProfileData?.user) {
      return null;
    }

    return userAddressToAddress(userProfileData.user, getUserState);
  }, [userProfileData?.user, getUserState]);

  // NOTE: We reset the C&C locationQuery on the ShippingOptions page to the users profile address
  useEffect(() => {
    userAddressAsAddress && setLocationQuery(userAddressAsAddress);
  }, [setLocationQuery, userAddressAsAddress]);

  useEffect(() => {
    if (flags[FF_ESCRIPT_RECONCILE_PRESCRIPTIONS] && customer?.isExternal && prescriptionIds.length !== 0) {
      mutateReconcilePrescriptions();
    }
  }, []);

  const isPrescriptionValid = useMemo(() => {
    // if the customer is not external, we do not need to reconcile and is valid
    if (!customer?.isExternal) {
      return true;
    }

    // If the selected products don't require any prescriptions, we don't need to check prescriptions
    if (prescriptionIds.length === 0) {
      return true;
    }

    if (customer?.isExternal && !isReconcilingPrescriptions) {
      // if there is an error, we consider the prescriptions valid to allow customers to proceed with the checkout
      if (isReconcilingPrescriptionsError) {
        return true;
      }
    }

    return reconcilePrescriptionsData?.valid ?? false;
  }, [
    customer?.isExternal,
    isReconcilingPrescriptions,
    isReconcilingPrescriptionsError,
    prescriptionIds.length,
    reconcilePrescriptionsData?.valid,
  ]);

  const location = useLocation();
  const state = location.state || {};
  const { orderId, orderCode } = state;

  const [approveSAS, setApproveSAS] = useState(false);
  const toggleApproveSAS = () => {
    setApproveSAS((v) => !v);
  };

  const [isDispenseIntervalDelayChecked, setIsDispenseIntervalDelayChecked] = useState(false);
  const [validIDCheckAccepted, setValidIdCheckAccepted] = useState(false);

  const areAnyProductsInDispenseInterval =
    selectedProducts?.some((product) => product.intervalState === 'PRE_ORDER') || false;
  const nextDispenseDate = !areAnyProductsInDispenseInterval
    ? null
    : (selectedProducts ?? [])
        .filter((product: PersistedProductDetails) => product.intervalState === 'PRE_ORDER')
        .map((product) => product.estimatedNextProcessDate)
        .sort()
        .at(0) ?? null;

  const dispenseIntervalDelayAcceptanceProps: DispenseIntervalDelayAcceptanceProps = {
    isChecked: isDispenseIntervalDelayChecked,
    setIsChecked: setIsDispenseIntervalDelayChecked,
    delayedUntilDate: nextDispenseDate || '',
  };

  const shouldShowSasAlert = Boolean(selectedData?.needSasApproval);

  const { placeOrderShopify, isSubmitting } = useShopifyCheckout({
    orderId: Number(orderId),
    orderCode: orderCode && String(orderCode),
    selectedProducts,
  });

  const {
    userData,
    formMethods,
    showManualAddressForm,
    handleAddressUpdate,
    handleSelectedAddress,
    handleResetAddress,
    handleManualAddressClick,
    handleCancelUpdate,
  } = useShippingAddress();

  const { deliveryOptions, isFetching: isFetchingDeliveryOptions, isError } = useDeliveryOptions();

  const sameDayAfternoonDeliveryOptionSettings = useSameDayAfternoonDeliveryOption(deliveryOptions.SameDayAfternoon);
  const sameDayDeliveryOptionSettings = useSameDayDeliveryOption(deliveryOptions.SameDay);
  const standardDeliveryOptionSettings = useStandardDeliveryOption(deliveryOptions.Standard);
  const clickAndCollectDeliveryOptionSettings = useClickAndCollectDeliveryOption(deliveryOptions.ClickAndCollect);

  const isLoading = isFetchingDeliveryOptions || isCustomerLoading || isAddressValidityLoading;

  const selectableDeliveryOptions = useMemo(
    () => ({
      [DELIVERY_OPTIONS[DELIVERY_OPTION.SameDayAfternoon]['name']]: sameDayAfternoonDeliveryOptionSettings,
      [DELIVERY_OPTIONS[DELIVERY_OPTION.SameDay]['name']]: sameDayDeliveryOptionSettings,
      [DELIVERY_OPTIONS[DELIVERY_OPTION.Standard]['name']]: standardDeliveryOptionSettings,
      [DELIVERY_OPTIONS[DELIVERY_OPTION.ClickAndCollect]['name']]: clickAndCollectDeliveryOptionSettings,
    }),
    [
      clickAndCollectDeliveryOptionSettings,
      sameDayAfternoonDeliveryOptionSettings,
      sameDayDeliveryOptionSettings,
      standardDeliveryOptionSettings,
    ],
  );

  const selectableDeliveryOptionNames: ShippingOptionsFormValues['shippingOption'][] = useMemo(() => {
    return Object.entries(selectableDeliveryOptions)
      .filter(([_optionName, optionProps]) => {
        return !!optionProps;
      })
      .map(([optionName]) => optionName as keyof typeof deliveryOptions);
  }, [selectableDeliveryOptions]);

  const { control, handleSubmit, setValue } = formMethods as UseFormReturn<ShippingOptionsFormValues>;

  const selectedShippingOption = formMethods.watch('shippingOption');
  const selectedOption =
    !!selectedShippingOption && deliveryOptions[selectedShippingOption as keyof typeof deliveryOptions][0];
  const selectedOptionExtraDetails = selectableDeliveryOptions[selectedShippingOption as keyof typeof deliveryOptions];

  const disableCheckoutDueToDispenseInterval = areAnyProductsInDispenseInterval && !isDispenseIntervalDelayChecked;
  const shouldShowDispenseIntervalDelayAcceptance = areAnyProductsInDispenseInterval && nextDispenseDate;
  const shouldShowDispenseIntervalAlert = areAnyProductsInDispenseInterval && nextDispenseDate;
  const shouldShowValidIdCheckbox = !!selectedOption && selectedOption.deliveryRequirements.identification === true;

  // NOTE: Needed to use layout effect to select the shipping option before it's rendered unselected to prevent the UI flashing
  useLayoutEffect(() => {
    if (!isLoading && !selectedShippingOption && selectableDeliveryOptionNames.length === 1) {
      setValue('shippingOption', selectableDeliveryOptionNames[0]);
      logger.info(`auto selecting single available option: ${selectableDeliveryOptionNames[0]}`, {
        selectableDeliveryOptions,
      });
    }
  }, [isLoading, selectableDeliveryOptionNames, selectableDeliveryOptions, selectedShippingOption, setValue]);

  const clearShippingOptionSelection = useCallback(() => setValue('shippingOption', undefined), [setValue]);
  useClearStaleShippingOptionSelection({
    selectedShippingOption,
    selectableDeliveryOptions,
    isLoading,
    clearSelection: clearShippingOptionSelection,
  });

  const handleCheckout = async (values: ShippingOptionsFormValues) => {
    try {
      const selectedOptionKey = values.shippingOption;
      if (!selectedOptionKey) {
        logger.error('No shipping option selected');
        return;
      }

      if (!selectedOption || !selectedOptionExtraDetails) {
        logger.error('No selectedOption found');
        return;
      }

      await placeOrderShopify({
        deliveryOption: {
          ...selectedOption,
          displayName: selectedOptionExtraDetails.title,
          pharmacyId: selectedOption.pharmacy.id,
        },
      });
    } catch (error: unknown) {
      setCheckoutError(error);
      logger.error('Shipping options checkout failed', error);

      if (isErrorResponse(error)) {
        // NOTE: Refetching the patientRefill endpoint here is just incase the error was caused by an existing unpaid admin order which doesn't have a static error.code yet
        // TODO: Update this to only invalidate on specific error code once BE has implemented that
        queryClient.invalidateQueries({ queryKey: client.order.getPatientRefill.keys });

        if (isMontuError(error)) {
          return toast.error(
            translateMontuError({
              error: error.body.error,
              fallbackOverride: 'Failed to process checkout',
            }),
          );
        }
      }

      return toast.error('Failed to process checkout');
    }
  };

  const handleClickAndCollect = () => {
    navigate({ pathname: PATIENT_CLICK_AND_COLLECT });
  };

  // NOTE: The following logic (before the returns) is analytics related
  const previouslySelectedShippingOption = usePrevious(selectedShippingOption);

  useEffect(() => {
    if (selectedShippingOption && selectedShippingOption !== previouslySelectedShippingOption) {
      emitEvent({
        component_name: 'ShippingOptions',
        event_name: 'shipping_select_option',
        id: 'shipping_select_option',
        value: selectedShippingOption,
      });
    }
  }, [emitEvent, previouslySelectedShippingOption, selectedShippingOption]);

  const previousShownShippingOptions = usePrevious(selectableDeliveryOptionNames);

  useEffect(() => {
    if (
      !isLoading &&
      selectableDeliveryOptionNames.length &&
      new Set(selectableDeliveryOptionNames).difference(new Set(previousShownShippingOptions)).size !== 0
    ) {
      const shownOptions = Object.entries(selectableDeliveryOptions)
        .filter(([_optionName, optionProps]) => {
          return !!optionProps;
        })
        .map(([optionName, optionProps]) => {
          return {
            name: optionName,
            estimated_arrival_time: optionProps?.estimatedArrivalTime,
          };
        });

      emitEvent({
        component_name: 'ShippingOptions',
        event_name: 'shipping_show_options',
        id: 'shipping_show_options',
        value: JSON.stringify(shownOptions),
      });
    }
  }, [selectableDeliveryOptions, emitEvent, previousShownShippingOptions, selectableDeliveryOptionNames, isLoading]);

  if (isError) {
    return (
      <ErrorPage
        title="Something went wrong"
        description={
          <>
            You can chat with us using the widget in your portal, reach out{' '}
            <Link
              href="https://support.umeds.com.au/hc/en-au/articles/36042289598745-Contact-uMeds"
              target="_blank"
              rel="noopener noreferrer"
            >
              here
            </Link>
            , or select Return to order medication to try again.
          </>
        }
        primaryText="Return to Order medication"
        primaryAction="/patient/refill"
        secondaryText=""
      />
    );
  }

  const isCheckoutDisabled =
    Boolean(customer?.isDischarged && !customer?.isExternal) ||
    isLoading ||
    isSubmitting ||
    (!approveSAS && shouldShowSasAlert) ||
    disableCheckoutDueToDispenseInterval ||
    !isPrescriptionValid ||
    (shouldShowValidIdCheckbox && !validIDCheckAccepted) ||
    !addressValidity.isValid;

  return (
    <FormProvider {...formMethods}>
      <PageContent>
        <Box>
          <BackButton />
          <PageSection sx={{ marginBottom: 3, gap: 6 }}>
            {flags[FF_BRAZE_CONTENT_CARDS] && <BrazeImageCard />}
            <PurchaseStepper stage="Shipping" />
          </PageSection>
          <PageSection sx={{ marginBottom: 4 }}>
            <Typography variant="h5" fontWeight={700}>
              Shipping options
            </Typography>
            {!showManualAddressForm && shouldShowDispenseIntervalAlert ? (
              <PageSection sx={{ marginTop: 4 }}>
                <PharmacyAllocationDispenseIntervalAlert reorderDate={formatDateForDisplay(nextDispenseDate)} />
              </PageSection>
            ) : null}
            {!showManualAddressForm && (
              <PageSection>
                <SasApprovalAlert enabled={shouldShowSasAlert} />
              </PageSection>
            )}
          </PageSection>
          <Grid2 container spacing={4}>
            <Grid2 size={{ xs: 12, md: 8 }}>
              <Typography variant="body1" fontWeight={600} marginBottom={3}>
                {showManualAddressForm ? 'Edit your address' : 'Review your address'}
              </Typography>
              {showManualAddressForm && userData ? (
                <ManualShippingAddress
                  onSubmit={handleAddressUpdate}
                  states={userData?.states}
                  onCancel={handleCancelUpdate}
                />
              ) : (
                <PageSection>
                  <ShippingAddress
                    errors={formMethods.formState.errors}
                    address={{ ...formMethods.getValues() }}
                    onSelectedAddress={handleSelectedAddress}
                    onResetAddress={handleResetAddress}
                    onManualAddressClick={handleManualAddressClick}
                  />
                  {addressValidity.errorMessage && (
                    <FormHelperText error>{addressValidity.errorMessage}</FormHelperText>
                  )}
                  <FormHelperText>Changing your address will refresh options</FormHelperText>
                </PageSection>
              )}
              <PageSection data-dd-privacy="allow">
                <Controller
                  name="shippingOption"
                  control={control}
                  rules={{ required: 'Select a delivery option' }}
                  render={({ field, fieldState }) => {
                    if (isLoading) {
                      return <LoadingShippingOptionCard />;
                    }

                    return (
                      <Stack rowGap={3} paddingTop={6}>
                        {!!selectableDeliveryOptionNames.length && (
                          <Typography
                            color={fieldState.error ? 'error' : 'text.primary'}
                            variant="body1"
                            fontWeight="600"
                          >
                            {selectableDeliveryOptionNames.length === 1
                              ? 'Delivery option'
                              : 'Select a delivery option'}
                          </Typography>
                        )}
                        {!!selectableDeliveryOptions.SameDayAfternoon && (
                          <SameDayAfternoonShippingOptionCard
                            {...selectableDeliveryOptions.SameDayAfternoon}
                            field={field}
                            fieldState={fieldState}
                          />
                        )}
                        {!!selectableDeliveryOptions.SameDay && (
                          <SameDayDeliveryShippingOptionCard
                            {...selectableDeliveryOptions.SameDay}
                            field={field}
                            fieldState={fieldState}
                          />
                        )}
                        {!!selectableDeliveryOptions.Standard && (
                          <StandardDeliveryShippingOptionCard
                            {...selectableDeliveryOptions.Standard}
                            field={field}
                            fieldState={fieldState}
                          />
                        )}
                        {!!selectableDeliveryOptions.ClickAndCollect && (
                          <ClickAndCollectShippingOptionCard
                            {...selectableDeliveryOptions.ClickAndCollect}
                            field={field}
                            fieldState={fieldState}
                            onClick={handleClickAndCollect}
                          />
                        )}
                      </Stack>
                    );
                  }}
                />
              </PageSection>
            </Grid2>

            <Grid2 size={{ xs: 12, md: 4 }} data-dd-privacy="allow">
              <Box data-dd-privacy="allow">
                <CheckoutOrderSummary
                  checkoutData={checkoutData}
                  isLoading={isLoading}
                  selectedOption={selectedOptionExtraDetails ?? undefined}
                  showShippingError={!!formMethods.formState.errors.shippingOption}
                  slots={{
                    terms: (
                      <Stack gap={2}>
                        {shouldShowSasAlert ? (
                          <TermsConditionCheckboxContainer data-testid="sas-consent">
                            <ConsentCheckbox
                              trackingId="SAS_Consent"
                              text={
                                <>
                                  I understand that my order may be delayed if my doctor needs&nbsp;
                                  <Link href={settings.regulationsApprovalsUrl}>
                                    SAS (Special Access Scheme) approval
                                  </Link>
                                  &nbsp;from the TGA. This process may take 5-7 business days before my medication can
                                  be dispensed.
                                </>
                              }
                              size={'small'}
                              checked={approveSAS}
                              onChange={toggleApproveSAS}
                            />
                          </TermsConditionCheckboxContainer>
                        ) : null}
                        {shouldShowDispenseIntervalDelayAcceptance && (
                          <DispenseIntervalDelayAcceptance {...dispenseIntervalDelayAcceptanceProps} />
                        )}
                        {shouldShowValidIdCheckbox && (
                          <ValidIDCheckbox
                            size="small"
                            value={validIDCheckAccepted}
                            onChange={setValidIdCheckAccepted}
                            deliveryText={'delivery'}
                          />
                        )}
                        <UmedsMarketingConsent size="small" />
                        <Typography variant={'caption'} marginLeft={0.5} marginTop={2}>
                          By proceeding, you agree with the{' '}
                          <Link href={UMEDS_MARKETING.TERMS_CONDITIONS} target="_blank" rel="noopener noreferrer">
                            uMeds Terms and Conditions
                          </Link>{' '}
                          and those of the pharmacy that will supply your medication.
                        </Typography>
                      </Stack>
                    ),
                  }}
                />
              </Box>
              <CheckoutButton
                disabled={isCheckoutDisabled}
                onClick={handleSubmit(handleCheckout)}
                loading={isSubmitting || isLoading}
                error={checkoutError}
              />
            </Grid2>
          </Grid2>
        </Box>
      </PageContent>
      <UseOrDeleteExistingOrderModal />
    </FormProvider>
  );
}
export default ShippingOptions;
